"use client";
import React from "react";
import { Box, Container, Typography, Button } from "@mui/material";
import Image from "next/image";
import NextLink from "next/link";
import { useTranslation } from "react-i18next";

const OMNI_ORANGE = "#C2501F";

// Same order as the JSON "agents" array.
// Jab images aa jayen to `image: null` ki jagah URL daal do.
const agentConfig = [
  {
    // Zayn
    roleColor: "#7A2BF5",
    gradient:
      "radial-gradient(circle at 30% 30%, #F9A8D4 0%, #C4B5FD 45%, #60A5FA 100%)",
    image: 'https://getgeek-website-assets.b-cdn.net/ZaynCircle.webp',
    href: "/ai-agents/zayn",
  },
  {
    // Scout
    roleColor: "#A020E8",
    gradient:
      "radial-gradient(circle at 30% 30%, #FDE68A 0%, #E879F9 50%, #A78BFA 100%)",
    image: 'https://getgeek-website-assets.b-cdn.net/ScoutCircle.webp',
    href: "/ai-agents/scout",
  },
  {
    // Sage
    roleColor: "#E0457B",
    gradient:
      "radial-gradient(circle at 30% 30%, #FDE68A 0%, #86EFAC 45%, #F472B6 100%)",
    image: 'https://getgeek-website-assets.b-cdn.net/SageCircle.webp',
    href: "/ai-agents/sage",
  },
  {
    // Atlas
    roleColor: "#1D8FDC",
    gradient:
      "radial-gradient(circle at 30% 30%, #A7F3D0 0%, #C4B5FD 50%, #93C5FD 100%)",
    image: 'https://getgeek-website-assets.b-cdn.net/AtlasCircle.webp',
    href: "/ai-agents/atlas",
  },
  {
    // Compass
    roleColor: "#D4790B",
    gradient:
      "radial-gradient(circle at 30% 30%, #FDE68A 0%, #FBCFE8 45%, #93C5FD 100%)",
    image: 'https://getgeek-website-assets.b-cdn.net/CompassCircle.webp',
    href: "/ai-agents/compass",
  },
];

const AgentAvatar = ({ image, gradient, alt }) => (
  <Box
    sx={{
      position: "relative",
      width: { xs: 44, md: 48 },
      height: { xs: 44, md: 48 },
      borderRadius: "50%",
      overflow: "hidden",
      background: gradient,
      flexShrink: 0,
      mb: { xs: 1.5, md: 2 },
    }}
  >
    {image && (
      <Image
        src={image}
        alt={alt}
        fill
        sizes="48px"
        style={{ objectFit: "cover" }}
      />
    )}
  </Box>
);

const OmniAiTeamSection = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const raw = t("omniAiTeam.agents", { returnObjects: true });
  const agents = Array.isArray(raw) ? raw : [];

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 6, sm: 8, md: 10 },
        backgroundColor: "#F7F4FD",
        direction: isArabic ? "rtl" : "ltr",
        mt: '5%'
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: { xs: 4, md: 6 } }}>
          <Typography
            sx={{
              fontSize: { xs: "10px", sm: "11px" },
              fontWeight: 600,
              color: OMNI_ORANGE,
              letterSpacing: "1.5px",
              textTransform: "uppercase",
              fontFamily: "var(--font-outfit)",
              mb: { xs: 1.5, sm: 2 },
            }}
          >
            {t("omniAiTeam.label")}
          </Typography>

          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "24px", sm: "30px", md: "34px", lg: "38px" },
              fontWeight: 700,
              lineHeight: 1.25,
              color: "#14122B",
              fontFamily: "var(--font-plusjakarta)",
              mb: { xs: 2, sm: 2.5 },
            }}
          >
            {t("omniAiTeam.heading")}
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: "14px", sm: "15px" },
              color: "#55556E",
              lineHeight: 1.8,
              fontWeight: 400,
              fontFamily: "var(--font-outfit)",
              maxWidth: 760,
              mx: "auto",
            }}
          >
            {t("omniAiTeam.description")}
          </Typography>
        </Box>

        {/* Cards grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
            },
            gap: { xs: 2, md: 2.5 },
          }}
        >
          {agents.map((agent, index) => {
            const cfg = agentConfig[index] || agentConfig[0];

            return (
              <NextLink
                key={index}
                href={cfg.href}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <Box
                  sx={{
                    height: "100%",
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #EDE6FA",
                    borderRadius: "14px",
                    p: { xs: 2.5, md: 3 },
                    textAlign: isArabic ? "right" : "left",
                    boxShadow: "0 2px 12px rgba(122,43,245,0.04)",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      transform: "translateY(-3px)",
                      boxShadow: "0 10px 28px rgba(122,43,245,0.12)",
                    },
                  }}
                >
                  <AgentAvatar
                    image={cfg.image}
                    // gradient={cfg.gradient}
                    alt={`${agent.name} AI agent`}
                  />

                  <Typography
                    sx={{
                      fontSize: { xs: "15px", md: "16px" },
                      fontWeight: 700,
                      color: "#14122B",
                      fontFamily: "var(--font-plusjakarta)",
                      lineHeight: 1.3,
                    }}
                  >
                    {agent.name}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: { xs: "10px", md: "11px" },
                      fontWeight: 500,
                      color: cfg.roleColor,
                      fontFamily: "var(--font-outfit)",
                      mb: { xs: 1, md: 1 },
                      mt: 0.7
                    }}
                  >
                    {agent.role}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: { xs: "13px", md: "13.5px" },
                      color: "#3D3A57",
                      lineHeight: 1.7,
                      fontWeight: 400,
                      fontFamily: "var(--font-outfit)",
                    }}
                  >
                    {agent.description}
                  </Typography>
                </Box>
              </NextLink>
            );
          })}

          {/* CTA card */}
          <Box
            sx={{
              backgroundColor: "#14122B",
              borderRadius: "14px",
              p: { xs: 3, md: 4 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: isArabic ? "flex-end" : "flex-start",
              gap: 2.5,
              minHeight: { xs: 160, md: "auto" },
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: "18px", md: "20px" },
                fontWeight: 700,
                lineHeight: 1.3,
                color: "#FFFFFF",
                fontFamily: "var(--font-plusjakarta)",
                textAlign: isArabic ? "right" : "left",
              }}
            >
              {t("omniAiTeam.cta.heading")}
            </Typography>

            <NextLink href="/ai-agents" passHref legacyBehavior>
              <Button
                component="a"
                sx={{
                  bgcolor: "#FFFFFF",
                  color: "#14122B",
                  px: 3,
                  py: 1,
                  borderRadius: "50px",
                  textTransform: "none",
                  fontSize: "13px",
                  fontWeight: 600,
                  fontFamily: "var(--font-outfit)",
                  whiteSpace: "nowrap",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    bgcolor: "#fff",
                    transform: "scale(1.03)",
                  },
                }}
              >
                {t("omniAiTeam.cta.label")}
              </Button>
            </NextLink>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default OmniAiTeamSection;